import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type TFn = (key: string, opts?: Record<string, unknown>) => string;
import toast from 'react-hot-toast';
import { getApiErrorMessage, getSubmissionAdvice } from '@/lib/apiError';
import {
  ArrowLeft,
  Users,
  Wallet,
  Banknote,
  Building2,
  CheckCircle2,
  PlayCircle,
  Lock,
  RotateCcw,
  Send,
  FileText,
  Download,
  Loader2,
  Landmark,
  ClipboardCheck,
  RefreshCw,
  Pencil,
  Check,
  X,
  Ban,
  Trash2,
  BadgeCheck,
} from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/features/shared/components/ui/tabs';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { Label } from '@/features/shared/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/features/shared/components/ui/dialog';
import { useAppSelector } from '@/app/hooks';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { cn } from '@/lib/utils';
import { payrollApi } from '../api/payrollApi';
import { downloadPayrollPackage } from '../api/register';
import type { RunDetail, ApprovalLevel, ExportType, ExportFormat, LockRunOptions, SubmissionDestination, RunExternalPaymentMethod } from '../types';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import { RUN_STATE_META, RUN_STATE_ORDER } from '../lib/state';
import { formatMoney, formatDate, COUNTRY_FLAG, COUNTRY_LABEL } from '../lib/format';
import { hasPay } from '../lib/runLines';
import { StatusChip } from '../components/StatusChip';
import { CancelWithReasonDialog } from '@/features/shared/components/CancelWithReasonDialog';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { RUN_CANCEL_REASONS, RUN_EXTERNAL_PAYMENT_METHODS } from '../types';
import { StatCard } from '../components/StatCard';
import { MarkPaidDialog } from '../components/MarkPaidDialog';
import { BankFileConflictDialog } from '../components/BankFileConflictDialog';
import { ControlCenterTab, OVERRIDE_REASON_MIN } from '../components/ControlCenterTab';
import { EmployeesTab } from '../components/EmployeesTab';
import { ApprovalsTab } from '../components/ApprovalsTab';
import { ExportsTab } from '../components/ExportsTab';
import { DisbursementsTab } from '../components/DisbursementsTab';
import AccountingPostCard from '../components/AccountingPostCard';
import CrossChargeCard from '../components/CrossChargeCard';
import { usePendingGuard } from '@/features/shared/components/recovery/useFormGuards';
import { RunActionNotice } from '../components/RunActionNotice';
import {
  lockOverrideRefusal,
  payrollActionNotice,
  refusedActionNotice,
  type LockOverrideRefusal,
  type PayrollAction,
  type PayrollActionNotice,
} from '../lib/payrollActionErrors';

/** Turn an API error into a clear, business-friendly (translated) message —
 *  especially the dual-control 409 a single operator hits when releasing their
 *  own batch, which is routed to their manager up the approval chain. */
function releaseErrorMessage(e: any, t: TFn): string {
  const status = e?.response?.status;
  const serverMsg = e?.response?.data?.message as string | undefined;
  if (status === 409 && /prepar|differ|duties|dual/i.test(serverMsg ?? '')) {
    return t('disbursement.dualControlManager');
  }
  return getApiErrorMessage(e, t('errors.generic'));
}

/** Progress banner for an async (large-run) calculation — the payroll worker
 *  processes the run in the background while this polls calc-status. */
const CalcProgress: React.FC<{ processed: number; total: number; t: TFn }> = ({
  processed,
  total,
  t,
}) => {
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  return (
    <div className="rounded-lg border border-primary-500/20 bg-primary-500/5 p-4">
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 font-medium text-primary-500">
          <PlayCircle className="h-4 w-4 animate-pulse" />
          {t('runDetail.calculating')}
        </span>
        <span className="tabular-nums text-muted-foreground">
          {t('runDetail.calcProgress', { processed, total })}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export const PayrollRunDetailPage: React.FC = () => {
  const { t } = useTranslation('payroll');
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The runs list lives at the path with the trailing "/runs/:id" removed.
  const backToList = () => navigate(pathname.replace(/\/runs\/[^/]+\/?$/, ''));
  const user = useAppSelector((s) => s.auth.user);
  const actor = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email : 'You';
  const { canApprovePayroll, canRunPayroll } = useRoleAccess();
  const { hasProductStrict } = useEntitlements();

  const [detail, setDetail] = useState<RunDetail | null>(null);
  // Renaming is inline rather than a dialog: it is a one-field correction, and a
  // modal for it would make naming a run feel like a bigger decision than it is.
  const [renaming, setRenaming] = useState(false);
  const [labelDraft, setLabelDraft] = useState('');
  const [stopOpen, setStopOpen] = useState(false);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  // Re-reading the run after its Employees read failed (Retry in that tab).
  const [retryingEmployees, setRetryingEmployees] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pkgBusy, setPkgBusy] = useState(false);
  // ?tab= deep-links a tab (e.g. the Finance hub links straight to Disbursements).
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const requested = searchParams.get('tab');
    return requested && ['control', 'employees', 'approvals', 'exports', 'disbursements'].includes(requested)
      ? requested
      : 'control';
  });
  // Set when a lock attempt was refused with 409 `risk_blocked` (open critical/
  // high alerts). Approvers can retry with an override reason from the dialog.
  const [lockBlocked, setLockBlocked] = useState<LockOverrideRefusal | null>(null);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  // Set when a release was refused with 409 `bank_file_issued` — a bank payment
  // file already exists for this run, so releasing would pay twice.
  const [bankFileConflict, setBankFileConflict] = useState<{ message: string } | null>(null);
  const [revertOpen, setRevertOpen] = useState(false);
  const [revertReason, setRevertReason] = useState('');
  // Statutory & Payment Submissions (pilot, entitlement-gated): per-destination
  // template availability from the server. null = not loaded / not entitled.
  const [destinations, setDestinations] = useState<SubmissionDestination[] | null>(null);
  const submissionsEnabled = hasProductStrict('statutory_submissions');
  // Async-calc progress (C-PERF-1 Phase 2): set while a large run is calculated by
  // the background worker; drives the progress banner. null when idle.
  const [calc, setCalc] = useState<{ processed: number; total: number } | null>(null);
  // Set when an approval/release action was refused with a Phase 4 code (approval_stale,
  // needs_recalc, recalculating, group_approver_required, segregation_of_duties, …): what
  // happens next, kept on screen after the toast is gone.
  const [notice, setNotice] = useState<PayrollActionNotice | null>(null);

  // The run this page shows NOW. A read answered after the person moved to
  // another run belongs to the old one and must never land on this page
  // (review HO-0mvnqm0hq001, follow-up to web #31).
  const shownRun = useRef(runId);
  shownRun.current = runId;
  useEffect(() => {
    if (!runId) return;
    payrollApi
      .getRunDetail(runId)
      .then((d) => { if (shownRun.current === runId) setDetail(d); })
      .catch(() => { if (shownRun.current === runId) setDetail(null); })
      .finally(() => { if (shownRun.current === runId) setLoading(false); });
  }, [runId]);

  useEffect(() => {
    // Best-effort: the submissions section simply doesn't render without it
    // (403 for un-piloted tenants is expected, not an error).
    if (!runId || !submissionsEnabled) return;
    payrollApi.getSubmissionDestinations(runId).then(setDestinations).catch(() => setDestinations(null));
  }, [runId, submissionsEnabled]);

  /** Record a manual submission: ensure the destination's filing exists for the
   *  run's period, then hand over to the Filings page (submit date, reference,
   *  acknowledgement upload, outcome — the full proven lifecycle lives there).
   *  Downloading a file NEVER marks it submitted; this is the explicit step. */
  async function recordSubmission(d: SubmissionDestination) {
    if (!detail) return;
    const end = new Date(detail.run.periodEnd);
    try {
      await payrollApi.generateFiling({
        legalEntityId: detail.run.legalEntityId,
        filingType: d.filingType,
        year: end.getUTCFullYear(),
        month: end.getUTCMonth() + 1,
      });
    } catch (e) {
      // An existing filing for the period is fine — we're navigating to it.
      const msg = getApiErrorMessage(e, '');
      if (!/exist/i.test(msg)) {
        toast.error(getApiErrorMessage(e, t('errors.generic', 'Could not open the submission record.')), { duration: 6000 });
        return;
      }
    }
    navigate(pathname.replace(/\/runs\/[^/]+\/?$/, '/filings'));
  }

  /** Silent refetch (no spinners/toasts) — used by the Disbursements tab for
   *  live settlement polling and after per-item retry/resolve actions. A failed
   *  poll keeps the last good detail instead of blanking the page. */
  const refreshDetail = useCallback(async () => {
    if (!runId) return;
    try {
      const d = await payrollApi.getRunDetail(runId);
      if (d) setDetail(d);
    } catch {
      /* transient poll failure — keep showing the last good state */
    }
  }, [runId]);

  async function downloadExport(id: string) {
    try {
      const url = await payrollApi.exportDownloadUrl(id);
      if (url) window.open(url, '_blank', 'noopener');
      else toast.error(t('errors.exportDownload'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('errors.exportDownload')), { duration: 6000 });
    }
  }

  // Excel preview (integration files): streams + downloads a readable workbook —
  // never the official submission file, never affects its checksum.
  async function previewExport(id: string) {
    try {
      await payrollApi.previewExport(id);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('errors.exportDownload')), { duration: 6000 });
    }
  }

  // Download the monthly payroll package (ZIP) for this run.
  async function downloadPackage() {
    if (!runId) return;
    setPkgBusy(true);
    try {
      const label = detail?.run?.periodLabel?.replace(/\s+/g, '-') || 'run';
      await downloadPayrollPackage(runId, `payroll-package-${label}.zip`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('errors.exportDownload')), { duration: 6000 });
    } finally {
      setPkgBusy(false);
    }
  }

  // Payroll has no dirty flag — its inputs are server-side — but it has
  // something more dangerous: money actions in flight. act() wraps
  // approveRun, lockRun and markRunPaid, and a reload during one of those
  // loses the ANSWER while the request may still land. The person is then
  // left not knowing whether the run was approved, or paid, and the obvious
  // response is to do it again.
  //
  // Concluding "no dirty flag, so reload is safe" would have missed exactly
  // this.
  usePendingGuard(busy, `payroll-run:${runId ?? 'unknown'}`, 'a payroll action');

  async function act<T>(fn: () => Promise<T>, success: string, action: PayrollAction = 'other') {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      const d = await payrollApi.getRunDetail(runId!);
      setDetail(d);
      toast.success(success);
    } catch (e: any) {
      await refusedAction(e, action, releaseErrorMessage(e, t));
    } finally {
      setBusy(false);
    }
  }

  /**
   * A refused payroll action. The toast is the server's own explanation; a Phase 4 code
   * additionally leaves a notice with the follow-up (and, for a stale approval or a
   * conflicting change, reloads the run so the chain shown is the one that now applies).
   * The one exception is the batch dual-control refusal (you PREPARED the payment), whose
   * existing wording explains the hand-off to a manager better than the server's one-liner.
   */
  async function refusedAction(e: unknown, action: PayrollAction, fallback: string) {
    // approve/reject always leave a notice (a role refusal, any 4xx, or an unknown outcome)
    // — a refused approval must not be a silent no-op once the toast fades.
    const n = refusedActionNotice(e, action);
    setNotice(n);
    const preparerRelease = action === 'release' && n?.title.startsWith('You prepared');
    toast.error(
      preparerRelease ? releaseErrorMessage(e, t) : getApiErrorMessage(e, n?.title ?? fallback),
      { duration: 6000 },
    );
    // reload: the refusal itself changed the run (e.g. unpaid leave cleared its approvals).
    if (n?.followUp === 'refresh' || n?.reload) await refreshDetail();
  }

  /** Validate the run. Small runs calculate synchronously (200 → refresh). Large
   *  runs are enqueued (202) and processed by the background worker — we poll
   *  calc-status and show a progress bar until it's done. (C-PERF-1 Phase 2.) */
  // Recalculate a validated run against current inputs. The server clears every
  // approval as part of this (SaveValidation deletes approval_workflows, because
  // an approval refers to numbers that no longer exist) — so the confirm says so
  // plainly rather than letting an approver discover their sign-off vanished.
  async function recalculate() {
    const approved = (detail?.approvals ?? []).filter((a: any) => a?.decision === 'approved').length;
    const ok = await confirm({
      title: t('runDetail.recalcTitle', { defaultValue: 'Recalculate this run?' }),
      message: approved > 0
        ? t('runDetail.recalcBodyApproved', {
          defaultValue:
            'This recomputes every employee against current inputs. {{count}} approval(s) already given will be cleared, because they refer to figures that will no longer exist — the run must be approved again.',
          count: approved,
        })
        : t('runDetail.recalcBody', {
          defaultValue:
            'This recomputes every employee against current inputs, picking up changes approved since the run was validated. Any approvals given are cleared and the run must be approved again.',
        }),
      confirmLabel: t('runDetail.recalculate', { defaultValue: 'Recalculate' }),
    });
    if (!ok) return;
    await validate();
  }

  async function validate() {
    if (!runId) return;
    setBusy(true);
    setNotice(null);
    try {
      const outcome = await payrollApi.validateRun(runId);
      if (outcome.async) {
        setCalc({ processed: 0, total: outcome.total });
        await pollCalc(runId);
      } else {
        const d = await payrollApi.getRunDetail(runId);
        setDetail(d);
        toast.success(t('run.validated'));
      }
    } catch (e: any) {
      // Per-employee blockers: the service replies 422 with {errors:[{employee_id,
      // reason}]} and NO message field — surface the reasons, not a generic toast,
      // and refresh the detail so the Employees tab shows each blocked line.
      const data = e?.response?.data;
      if (e?.response?.status === 422 && Array.isArray(data?.errors) && data.errors.length) {
        const reasons = Array.from(new Set(data.errors.map((x: any) => x?.reason).filter(Boolean))) as string[];
        const headline = t('run.validate_blocked', {
          defaultValue: '{{count}} employee(s) could not be calculated',
          count: data.errors.length,
        });
        toast.error(reasons.length ? `${headline}: ${reasons.slice(0, 2).join('; ')}` : headline, { duration: 8000 });
        try {
          const d = await payrollApi.getRunDetail(runId);
          setDetail(d);
        } catch { /* keep the current view */ }
      } else {
        await refusedAction(e, 'other', t('errors.generic'));
      }
    } finally {
      setBusy(false);
      setCalc(null);
    }
  }

  /** Poll async-calc status to completion (bounded so a stuck job can't loop
   *  forever). Updates the progress banner each tick. */
  async function pollCalc(id: string) {
    const maxTicks = 600; // ~15 min at 1.5s
    for (let i = 0; i < maxTicks; i++) {
      await new Promise((r) => setTimeout(r, 1500));
      const s = await payrollApi.getCalcStatus(id);
      if (!s) continue;
      setCalc({ processed: s.processed, total: s.total });
      if (s.status === 'done') {
        const d = await payrollApi.getRunDetail(id);
        setDetail(d);
        toast.success(t('run.validated'));
        return;
      }
      if (s.status === 'failed') {
        toast.error(s.error || t('errors.generic'), { duration: 6000 });
        return;
      }
    }
    toast.error(t('errors.generic'), { duration: 6000 });
  }

  /** Lock the run. A 409 `risk_blocked` (open critical/high alerts) or
   *  `unpaid_leave_held` (unpaid leave payroll couldn't apply) opens the blocked
   *  dialog instead of a toast; approvers can retry with an audited override. */
  async function lock(opts?: LockRunOptions) {
    setBusy(true);
    setNotice(null);
    try {
      const d = await payrollApi.lockRun(runId!, opts);
      setDetail(d);
      setLockBlocked(null);
      toast.success(t('run.locked'));
    } catch (e: any) {
      const blocked = lockOverrideRefusal(e);
      // Any other refusal closes the override dialog (DEF-0mvn6l4ib001): e.g.
      // unpaid_leave_not_calculated has already sent the run back to validated,
      // so there is nothing left to override — the notice below says to
      // recalculate, over the reloaded run.
      setLockBlocked(blocked);
      if (blocked) {
        // Unpaid leave not applied also stays on the page once the dialog is closed.
        if (blocked.kind === 'unpaid_leave_held') setNotice(payrollActionNotice(e, 'lock'));
      } else {
        await refusedAction(e, 'lock', t('errors.generic'));
      }
    } finally {
      setBusy(false);
    }
  }

  // Naming a run is allowed in ANY state: a label describes a run, it is not part
  // of its identity, and a locked run is exactly the one you most need to be able
  // to tell apart later.
  async function saveLabel() {
    if (!runId) return;
    const next = labelDraft.trim();
    setBusy(true);
    try {
      await payrollApi.setRunLabel(runId, next);
      setRenaming(false);
      await refreshDetail();
      toast.success(next
        ? t('runDetail.renamed', { defaultValue: 'Run renamed.' })
        : t('runDetail.nameCleared', { defaultValue: 'Name removed.' }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('runDetail.renameFailed', {
        defaultValue: 'Could not rename the run.',
      })));
    } finally {
      setBusy(false);
    }
  }

  /** Record that this locked run was settled outside KaziPeople. Reaching `paid`
   *  is what makes payslips visible in My Pay, so the toast says that rather than
   *  naming a state the person who pressed the button does not think in. */
  async function doMarkPaid(body: { method: RunExternalPaymentMethod; reference: string; note: string }) {
    if (!runId) return;
    setBusy(true);
    setNotice(null);
    try {
      await payrollApi.markRunPaid(runId, body);
      setMarkPaidOpen(false);
      await refreshDetail();
      toast.success(t('markPaid.done', {
        defaultValue: 'Recorded. Employees can now see their payslips in My Pay.',
      }));
    } catch (e) {
      // The server's refusals name the state and what to do instead ("it is
      // approved — lock the run first, which freezes the figures and generates
      // payslips"). Substituting our own wording would throw that away, so a
      // REJECTION still shows the server's text.
      //
      // ⚠️ But an unanswered request is different. Recording a payment is not
      // repeatable, and a 5xx means the server was REACHED and may have
      // committed before falling over. "Could not record the payment" would be
      // a claim we cannot support, and acting on it records it twice.
      const advice = getSubmissionAdvice(e, { consequential: true });
      // A Phase 4 release refusal (approval_stale / segregation_of_duties / …) is a clear
      // REJECTION: keep its follow-up on screen.
      if (advice.outcome === 'rejected') setNotice(payrollActionNotice(e, 'mark_paid'));
      toast.error(
        advice.outcome === 'rejected'
          ? getApiErrorMessage(e, t('markPaid.failed', { defaultValue: 'Could not record the payment.' }))
          : advice.message,
        { duration: advice.action === 'check' ? 12000 : 6000 },
      );
      if (advice.action === 'check') {
        // Show them what actually happened instead of asking them to guess.
        await refreshDetail();
      }
    } finally {
      setBusy(false);
    }
  }

  /** Release the prepared batch. A 409 `bank_file_issued` opens the conflict
   *  dialog instead of a toast: a bank file already exists for this run, and
   *  releasing as well pays these employees twice. A toast would be dismissed. */
  async function releaseBatch(override?: { reason: string }) {
    setBusy(true);
    setNotice(null);
    try {
      await payrollApi.submitBatch(run!.id, actor,
        override ? { override: true, overrideReason: override.reason } : undefined);
      setBankFileConflict(null);
      const d = await payrollApi.getRunDetail(runId!);
      setDetail(d);
      toast.success(t('disbursement.released'));
    } catch (e: any) {
      if (e?.response?.status === 409 && e?.response?.data?.code === 'bank_file_issued') {
        setBankFileConflict({
          message: e.response.data.message ||
            t('bankFileConflict.default', 'A bank payment file was already generated for this run.'),
        });
      } else {
        await refusedAction(e, 'release', t('errors.generic'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function doCancel({ reason, note }: { reason?: string; note?: string }) {
    if (!runId) return;
    setBusy(true);
    try {
      await payrollApi.cancelRun(runId, { reason, note });
      setStopOpen(false);
      await refreshDetail();
      toast.success(t('runDetail.cancelled', { defaultValue: 'Run cancelled.' }));
    } catch (e) {
      // The server's refusals name the state and the right action instead
      // ("this run is paid — reversing a closed period is a separate action").
      // Substituting our own wording here would throw that away.
      toast.error(getApiErrorMessage(e, t('runDetail.cancelFailed', {
        defaultValue: 'Could not cancel the run.',
      })));
    } finally {
      setBusy(false);
    }
  }

  async function doDiscard(lineCount: number) {
    if (!runId) return;
    const ok = await confirm({
      title: t('runDetail.discardTitle', { defaultValue: 'Discard this draft run?' }),
      message: t('runDetail.discardBody', {
        // Unlike an expense report, a discarded RUN really is removed — see the
        // ADR. So this copy says so, and also says what survives, because "the
        // run is gone" and "there is no record it existed" are different claims
        // and only the first one is true.
        defaultValue:
          'Nothing has been calculated and nobody has approved it, so the run itself is removed. The audit log keeps a record that it existed and who discarded it. This cannot be undone.',
      }),
      confirmLabel: t('runDetail.discardConfirm', { defaultValue: 'Discard run' }),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await payrollApi.discardRun(runId);
      toast.success(t('runDetail.discarded', { defaultValue: 'Draft run discarded.' }));
      backToList();
    } catch (e) {
      // Reachable in the race where lines appeared between render and click: the
      // server refuses and explains that discarding would throw a calculation
      // away. It must never quietly cancel instead.
      void lineCount;
      toast.error(getApiErrorMessage(e, t('runDetail.discardFailed', {
        defaultValue: 'Could not discard the run.',
      })));
    } finally {
      setBusy(false);
    }
  }

  /** Retry after the Employees read failed: re-read the whole detail so the
   *  headcount, tab label and list all come from one answer. */
  async function retryEmployees() {
    if (!runId) return;
    const asked = runId;
    setRetryingEmployees(true);
    try {
      const d = await payrollApi.getRunDetail(asked);
      // Moved to another run meanwhile: this answer is the old run's — drop it.
      if (d && shownRun.current === asked) setDetail(d);
    } catch (e) {
      if (shownRun.current !== asked) return;
      toast.error(getApiErrorMessage(e, t('runDetail.employeesUnavailable', {
        defaultValue: 'The employee list for this run could not be loaded.',
      })), { duration: 6000 });
    } finally {
      setRetryingEmployees(false);
    }
  }

  if (loading) return <DetailSkeleton />;
  if (!detail) {
    return (
      <div className="py-16 text-center">
        <p className="text-muted-foreground">{t('runDetail.notFound')}</p>
        <Button variant="outline" className="mt-4" onClick={backToList}>
          {t('runDetail.backToPayroll')}
        </Button>
      </div>
    );
  }

  const { run, employees, approvals, exports, batch } = detail;
  const currency = run.currency;
  // Mirrors payrollrun.CancellableFrom / Discardable. The server is the
  // authority — this only decides which button to OFFER, and a stale view simply
  // gets a refusal that explains itself.
  const cancellable = run.state === 'draft' || run.state === 'validated' || run.state === 'approved';
  const reasonRequired = run.state === 'validated' || run.state === 'approved';
  // payrollrun.Discardable counts CALCULATED lines: the Employees list now
  // also carries errored/excluded lines, which do not make a draft keepable.
  //
  // A FAILED Employees read is unknown, not empty (DEF-0mvnq1snl001-4c48d4):
  // no headcount, no tab count, no Discard and no picker come from it.
  const employeesKnown = !detail.employeesError;
  const calculatedLines = employees.filter(hasPay).length;
  const discardable = run.state === 'draft' && employeesKnown && calculatedLines === 0;

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={backToList}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-charcoal"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('runDetail.payrollRuns')}
      </button>

      <CancelWithReasonDialog
        open={stopOpen}
        busy={busy}
        onClose={() => setStopOpen(false)}
        onConfirm={doCancel}
        reasons={RUN_CANCEL_REASONS.map((r) => ({
          code: r.code,
          label: t(`runs.cancelReason.${r.code}`, { defaultValue: r.label }),
        }))}
        reasonRequired={reasonRequired}
        title={t('runDetail.cancelTitle', { defaultValue: 'Cancel this payroll run' })}
        description={t('runDetail.cancelBody', {
          defaultValue:
            'The run stays on the record as cancelled — nothing is deleted, and no pay is affected. It cannot be validated or approved afterwards.',
        })}
        confirmLabel={t('runDetail.cancelRun', { defaultValue: 'Cancel run' })}
        keepLabel={t('runDetail.keepRun', { defaultValue: 'Keep it' })}
        notePlaceholder={t('runDetail.cancelNotePh', { defaultValue: 'e.g. replaced by the corrected May run' })}
      />

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-6">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-h1 text-charcoal">{run.periodLabel}</h1>
            <StatusChip meta={RUN_STATE_META[run.state]} />
            {renaming ? (
              <span className="inline-flex items-center gap-1">
                <input
                  autoFocus
                  value={labelDraft}
                  maxLength={80}
                  onChange={(e) => setLabelDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void saveLabel();
                    if (e.key === 'Escape') setRenaming(false);
                  }}
                  aria-label={t('runDetail.nameField', { defaultValue: 'Run name' })}
                  placeholder={t('runDetail.namePlaceholder', { defaultValue: 'e.g. Bonus run' })}
                  className="rounded-md border border-border bg-background px-2 py-1 text-sm"
                />
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => void saveLabel()}
                  aria-label={t('runDetail.saveName', { defaultValue: 'Save name' })}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRenaming(false)}
                  aria-label={t('runDetail.cancelRename', { defaultValue: 'Stop renaming' })}>
                  <X className="h-4 w-4" />
                </Button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => { setLabelDraft(run.label ?? ''); setRenaming(true); }}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-charcoal"
              >
                {run.label
                  ? <span className="font-medium text-charcoal">{run.label}</span>
                  : t('runDetail.nameRun', { defaultValue: 'Name this run' })}
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Building2 className="h-4 w-4" />
              {run.legalEntityName}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden>{COUNTRY_FLAG[run.country]}</span>
              {COUNTRY_LABEL[run.country]} · {currency}
            </span>
            <span>
              {formatDate(run.periodStart)} – {formatDate(run.periodEnd)}
            </span>
          </p>

          {/* A cancelled run has to explain itself here, or the only way to find
              out why is the audit log. */}
          {run.state === 'cancelled' && (
            <p className="mt-3 rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              <Ban className="mr-1.5 inline h-4 w-4" />
              {run.cancelReason
                ? t('runDetail.cancelledBecause', {
                  defaultValue: 'Cancelled — {{reason}}',
                  reason: t(`runs.cancelReason.${run.cancelReason}`, {
                    defaultValue: run.cancelReason.replace(/_/g, ' '),
                  }),
                })
                : t('runDetail.cancelledPlain', { defaultValue: 'Cancelled.' })}
              {run.cancelNote ? ` · ${run.cancelNote}` : ''}
            </p>
          )}

          {/* A run can reach `paid` two ways and they are not the same fact: a
              payment KaziPeople executed, or a human asserting the money left by
              another route. Without this line the word "paid" covers both, and
              nobody reviewing later can tell which. */}
          {run.paidExternallyAt && (
            <p className="mt-3 rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              <BadgeCheck className="mr-1.5 inline h-4 w-4" />
              {t('markPaid.recordedAs', {
                defaultValue: 'Paid outside KaziPeople — {{method}}, recorded {{date}}',
                method: t(`markPaid.method.${run.externalPaymentMethod}`, {
                  defaultValue:
                    RUN_EXTERNAL_PAYMENT_METHODS.find((m) => m.code === run.externalPaymentMethod)?.label
                    ?? run.externalPaymentMethod?.replace(/_/g, ' ')
                    ?? '',
                }),
                date: formatDate(run.paidExternallyAt),
              })}
              {run.externalPaymentReference ? ` · ${run.externalPaymentReference}` : ''}
              {run.externalPaymentNote ? ` · ${run.externalPaymentNote}` : ''}
            </p>
          )}

          {/* Discard and Cancel are never both offered: which applies is a
              property of the run, not a choice to put to the user. An empty draft
              goes away; anything with a calculation or an approver is kept. */}
          {(cancellable || discardable) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {discardable ? (
                <Button variant="outline" size="sm" className="gap-2 text-danger-strong hover:text-danger-strong"
                  disabled={busy} onClick={() => void doDiscard(calculatedLines)}>
                  <Trash2 className="h-4 w-4" />
                  {t('runDetail.discard', { defaultValue: 'Discard draft' })}
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="gap-2 text-danger-strong hover:text-danger-strong"
                  disabled={busy} onClick={() => setStopOpen(true)}>
                  <Ban className="h-4 w-4" />
                  {t('runDetail.cancelRun', { defaultValue: 'Cancel run' })}
                </Button>
              )}
            </div>
          )}
          {run.state !== 'draft' && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('register')}>
                <FileText className="h-4 w-4" />
                Payroll register
              </Button>
              <Button variant="outline" size="sm" className="gap-2" disabled={pkgBusy} onClick={downloadPackage}>
                {pkgBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Download package
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => {
                  const [y, m] = run.periodStart.split('-');
                  navigate(`../../finance?tab=filings&year=${y}&month=${Number(m)}`);
                }}
              >
                <Landmark className="h-4 w-4" />
                Statutory filings
              </Button>
            </div>
          )}
        </div>
        <PrimaryAction
          state={run.state}
          busy={busy}
          // Once you've moved to Disbursements (or a batch already exists), the
          // "Pay employees" CTA is spent — disable it so it can't be re-clicked.
          payStarted={tab === 'disbursements' || !!detail?.batch}
          onValidate={validate}
          onRecalculate={() => void recalculate()}
          onLock={() => lock()}
          onPay={() => setTab('disbursements')}
          onReopen={() => setReopenOpen(true)}
          // permApprove on the server: this asserts money moved AND reveals every
          // figure in the run to every employee in it. Offering it to someone who
          // will be refused is worse than not offering it.
          canMarkPaid={canApprovePayroll}
          onMarkPaid={() => setMarkPaidOpen(true)}
          onRevert={() => { setRevertReason(''); setRevertOpen(true); }}
          onGoToApprovals={() => setTab('approvals')}
        />
      </div>

      {/* Async-calc progress banner (C-PERF-1 Phase 2): shown while a large run is
          calculated by the background worker. */}
      {calc && <CalcProgress processed={calc.processed} total={calc.total} t={t} />}

      <RunActionNotice
        notice={notice}
        busy={busy}
        onRecalculate={() => { setNotice(null); void recalculate(); }}
        onRefresh={() => { setNotice(null); void refreshDetail(); }}
        onGoToApprovals={() => { setNotice(null); setTab('approvals'); }}
        onDismiss={() => setNotice(null)}
      />

      {/* Lifecycle stepper */}
      <Lifecycle state={run.state} />

      {/* Totals */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Wallet} label={t('runDetail.statGross')} value={formatMoney(run.totals.gross, currency)} />
        <StatCard icon={Banknote} label={t('runDetail.statNetPay')} value={formatMoney(run.totals.net, currency)} tone="success" />
        <StatCard icon={Building2} label={t('runDetail.statEmployerCost')} value={formatMoney(run.totals.employerCost, currency)} tone="info" />
        <StatCard
          icon={Users}
          label={t('runDetail.statEmployees')}
          // Show the whole in-run headcount, then how many are calculated — so the card can't
          // read "3 · all calculated" while a 4th employee sits uncalculated in the run.
          value={employeesKnown ? String(run.employeeCount) : '—'}
          hint={
            !employeesKnown
              ? t('runDetail.employeesUnknownHint', { defaultValue: 'Employee list unavailable' })
              : run.calculated < run.employeeCount
              ? t('runDetail.someCalculated', {
                  calculated: run.calculated,
                  total: run.employeeCount,
                  defaultValue: '{{calculated}} of {{total}} calculated',
                })
              : run.errored > 0
                ? t('runDetail.excludedErrors', { count: run.errored })
                : t('runDetail.allCalculated')
          }
          tone={!employeesKnown || run.calculated < run.employeeCount || run.errored > 0 ? 'warning' : 'default'}
        />
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="control">
                {t('runDetail.tabControlCenter', { defaultValue: 'Control Center' })}
              </TabsTrigger>
              <TabsTrigger value="employees">
                {employeesKnown
                  ? t('runDetail.tabEmployees', { count: employees.length })
                  : t('runDetail.tabEmployeesUnknown', { defaultValue: 'Employees (—)' })}
              </TabsTrigger>
              <TabsTrigger value="approvals">{t('runDetail.tabApprovals')}</TabsTrigger>
              <TabsTrigger value="exports">{t('runDetail.tabExports', { count: exports.length })}</TabsTrigger>
              <TabsTrigger value="disbursements">{t('runDetail.tabDisbursements')}</TabsTrigger>
            </TabsList>

            <TabsContent value="control">
              <ControlCenterTab runId={run.id} currency={currency} />
            </TabsContent>
            <TabsContent value="employees">
              <EmployeesTab
                employees={employees}
                currency={currency}
                periodMonth={run.periodStart?.slice(0, 7)}
                canEdit={canRunPayroll && (run.state === 'draft' || run.state === 'validated')}
                unavailable={detail.employeesError}
                onRetry={() => void retryEmployees()}
                retrying={retryingEmployees}
              />
            </TabsContent>
            <TabsContent value="approvals">
              <ApprovalsTab
                approvals={approvals}
                runState={run.state}
                run={run}
                busy={busy}
                onApprove={(level: ApprovalLevel) =>
                  act(() => payrollApi.approveRun(run.id, level, actor), t('run.approved', { level }), 'approve')
                }
                onReject={(note) => act(() => payrollApi.rejectRun(run.id, note), t('run.reopened'), 'reject')}
              />
            </TabsContent>
            <TabsContent value="exports">
              <ExportsTab
                exports={exports}
                runState={run.state}
                busy={busy}
                onRequest={(type: ExportType, format: ExportFormat, force?: boolean) =>
                  act(() => payrollApi.requestExport(run.id, type, format, force), t('run.exportGenerated'))
                }
                onDownload={downloadExport}
                onPreview={previewExport}
                onReviewEmployees={() => setTab('employees')}
                destinations={destinations ?? undefined}
                submissionsEnabled={submissionsEnabled}
                onRecordSubmission={recordSubmission}
              />
            </TabsContent>
            <TabsContent value="disbursements">
              <DisbursementsTab
                batch={batch}
                runState={run.state}
                currency={currency}
                busy={busy}
                onPrepare={() => act(() => payrollApi.prepareBatch(run.id, actor), t('disbursement.prepared'))}
                onSubmit={() => void releaseBatch()}
                onCancel={() =>
                  batch && act(() => payrollApi.cancelBatch(batch.id), t('disbursement.cancelled', { defaultValue: 'Batch cancelled — nothing was released.' }))
                }
                onRefresh={refreshDetail}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Post this run's journal to the tenant's accounting system. Renders
          nothing unless a ledger is connected and the run is approved. */}
      <AccountingPostCard runId={run.id} runState={run.state} legalEntityId={run.legalEntityId} />
      {/* Renders nothing unless cost was actually recharged to another
          company, which is every run in a single-entity group. */}
      <CrossChargeCard runId={run.id} runState={run.state} />

      {/* Lock refused (409 risk_blocked): explain, and let approvers override. */}
      <LockBlockedDialog
        info={lockBlocked}
        busy={busy}
        canOverride={canApprovePayroll}
        onCancel={() => setLockBlocked(null)}
        onOverride={(reason) => lock({ override: true, overrideReason: reason })}
      />

      {/* Revert an APPROVED (unlocked) run back to draft so it can be corrected and re-run. */}
      <Dialog open={revertOpen} onOpenChange={setRevertOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('runDetail.revertTitle', { defaultValue: 'Revert this run to draft?' })}</DialogTitle>
            <DialogDescription>
              {t('runDetail.revertDesc', {
                defaultValue:
                  'This un-approves the run and moves it back to Draft so you can correct and re-run it. All approvals are cleared and it must be re-validated and re-approved. The preparer and everyone who approved are notified with your reason.',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-1">
            <Label className="text-xs">{t('runDetail.revertReason', { defaultValue: 'Reason (shared with the preparer & approvers)' })}</Label>
            <Textarea rows={3} value={revertReason} onChange={(e) => setRevertReason(e.target.value)} placeholder={t('runDetail.revertReasonPh', { defaultValue: 'e.g. a salary correction is needed before this is paid' })} />
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setRevertOpen(false)}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              disabled={busy || !revertReason.trim()}
              onClick={() => {
                setRevertOpen(false);
                void act(() => payrollApi.revertRun(run.id, revertReason.trim()), t('run.reverted', { defaultValue: 'Run reverted to draft' }));
              }}
            >
              {t('runDetail.revertConfirm', { defaultValue: 'Revert to draft' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Release refused: a bank payment file already exists for this run. */}
      <BankFileConflictDialog
        info={bankFileConflict}
        busy={busy}
        onCancel={() => setBankFileConflict(null)}
        onOverride={(reason) => void releaseBatch({ reason })}
      />

      {/* Record an outside settlement (bank file / transfer / cash) → paid, which
          is what makes payslips visible in My Pay. */}
      <MarkPaidDialog
        open={markPaidOpen}
        busy={busy}
        employeeCount={employeesKnown ? run.employeeCount : null}
        batchStatus={batch?.status}
        onClose={() => setMarkPaidOpen(false)}
        onConfirm={(body) => void doMarkPaid(body)}
      />

      {/* Reopen a locked run back to draft to re-calculate (e.g. after approving payout methods). */}
      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('runDetail.reopenTitle', { defaultValue: 'Reopen this run?' })}</DialogTitle>
            <DialogDescription>
              {t('runDetail.reopenDesc', {
                defaultValue:
                  'This moves the run back to Draft so you can re-calculate it — for example to pick up payout methods approved after it was locked. All approvals are cleared and it must be re-validated, re-approved and re-locked. It is refused if a disbursement is already in progress.',
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setReopenOpen(false)}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                setReopenOpen(false);
                void act(() => payrollApi.reopenRun(run.id, ''), t('run.reopened'));
              }}
            >
              {t('runDetail.reopenConfirm', { defaultValue: 'Reopen to draft' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const LockBlockedDialog: React.FC<{
  info: LockOverrideRefusal | null;
  busy: boolean;
  canOverride: boolean;
  onCancel: () => void;
  onOverride: (reason: string) => void;
}> = ({ info, busy, canOverride, onCancel, onOverride }) => {
  const { t } = useTranslation('payroll');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (info) setReason('');
  }, [info]);
  const tooShort = reason.trim().length < OVERRIDE_REASON_MIN;
  const leave = info?.kind === 'unpaid_leave_held';

  return (
    <Dialog open={!!info} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {leave
              ? t('controlCenter.lockBlockedLeaveTitle', { defaultValue: 'Lock blocked: unpaid leave not applied' })
              : t('controlCenter.lockBlockedTitle', { defaultValue: 'Lock blocked by risk alerts' })}
          </DialogTitle>
          <DialogDescription>
            {info?.message ||
              (leave
                ? t('controlCenter.lockBlockedLeaveDefault', {
                  defaultValue: 'Some unpaid leave could not be applied to this payroll — paying it now would leave it out.',
                })
                : t('controlCenter.lockBlockedDefault', 'Open critical or high risk alerts are blocking this run.'))}
          </DialogDescription>
        </DialogHeader>
        {canOverride ? (
          <>
            <p className="text-sm text-muted-foreground">
              {leave
                ? t('controlCenter.lockOverrideLeaveHint', {
                  defaultValue:
                    'Review "Unpaid leave not applied" on the Control Center tab, or override and lock anyway. Your reason is kept on the audit trail.',
                })
                : t('controlCenter.lockOverrideHint', {
                  defaultValue:
                    'Review the alerts on the Control Center tab, or override and lock anyway. Your reason is kept on the audit trail.',
                })}
            </p>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('controlCenter.overrideReasonPlaceholder', {
                defaultValue: 'Why is it safe to proceed? (a brief reason, e.g. NA)',
              })}
            />
            {tooShort && reason.length > 0 && (
              <p className="text-xs text-danger">
                {t('controlCenter.overrideReasonTooShort', {
                  defaultValue: 'Please enter a reason.',
                })}
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onCancel} disabled={busy}>
                {t('controlCenter.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                variant="destructive"
                disabled={busy || tooShort}
                onClick={() => onOverride(reason.trim())}
              >
                {t('controlCenter.overrideAndLock', { defaultValue: 'Override and lock' })}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {leave
                ? t('controlCenter.lockBlockedLeaveNonApprover', {
                  defaultValue:
                    'Review "Unpaid leave not applied" on the Control Center tab, or ask a payroll approver to override.',
                })
                : t('controlCenter.lockBlockedNonApprover', {
                  defaultValue:
                    'Resolve the open alerts on the Control Center tab, or ask a payroll approver to override.',
                })}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={onCancel}>
                {t('controlCenter.close', { defaultValue: 'Close' })}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PrimaryAction: React.FC<{
  state: RunDetail['run']['state'];
  busy: boolean;
  payStarted: boolean;
  onValidate: () => void;
  onRecalculate: () => void;
  onLock: () => void;
  onPay: () => void;
  onReopen: () => void;
  onRevert: () => void;
  onGoToApprovals: () => void;
  canMarkPaid: boolean;
  onMarkPaid: () => void;
}> = ({ state, busy, payStarted, onValidate, onRecalculate, onLock, onPay, onReopen, onRevert, onGoToApprovals, canMarkPaid, onMarkPaid }) => {
  const { t } = useTranslation('payroll');
  switch (state) {
    case 'draft':
      return (
        <Button className="gap-2" disabled={busy} onClick={onValidate}>
          <PlayCircle className="h-4 w-4" />
          {t('runDetail.validateRun')}
        </Button>
      );
    case 'validated':
      // Two actions, because a validated run is not finished with: it is waiting
      // for approvals, AND its inputs can still change underneath it.
      //
      // RECALCULATE exists because inputs approved after validation — a benefit
      // enrolment, a payout method, a corrected salary — do not reach a run that
      // has already been calculated. Without it the only way through was
      // "Reject & reopen" with an invented reason, which put a fiction in the
      // approval trail in order to do something legitimate.
      //
      // It is NOT "Refresh checks". That button re-reads the readiness checks and
      // deliberately changes no money; a control labelled "refresh" that silently
      // rewrites everyone's net pay would be a trap.
      return (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" className="gap-2" disabled={busy} onClick={onRecalculate}>
            <RefreshCw className="h-4 w-4" />
            {t('runDetail.recalculate', { defaultValue: 'Recalculate' })}
          </Button>
          <Button
            variant="ghost"
            className="gap-2 rounded-lg bg-info/10 px-3 py-2 text-sm font-medium text-info hover:bg-info/20"
            onClick={onGoToApprovals}
          >
            <ClipboardCheck className="h-4 w-4" />
            {t('runDetail.awaitingApprovals')}
          </Button>
        </div>
      );
    case 'approved':
      return (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" className="gap-2" disabled={busy} onClick={onRevert}>
            <RotateCcw className="h-4 w-4" />
            {t('runDetail.revertRun', { defaultValue: 'Revert to draft' })}
          </Button>
          <Button className="gap-2" disabled={busy} onClick={onLock}>
            <Lock className="h-4 w-4" />
            {t('runDetail.lockRun')}
          </Button>
        </div>
      );
    case 'locked':
      // THREE actions, because a locked run has three honest futures: correct it,
      // pay it here, or record that it was already paid elsewhere.
      //
      // "Already paid" is not a lesser option — for a tenant that pays by bank
      // file it is the ONLY route, and while it was missing their employees could
      // never see a payslip. It stays an outline button next to the primary CTA
      // rather than hidden in a menu: equally reachable, but "Pay employees" is
      // still what moves money, and only one of the two should look like it does.
      return (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" className="gap-2" disabled={busy} onClick={onReopen}>
            <RotateCcw className="h-4 w-4" />
            {t('runDetail.reopenRun', { defaultValue: 'Reopen to edit' })}
          </Button>
          {canMarkPaid && (
            <Button variant="outline" className="gap-2" disabled={busy} onClick={onMarkPaid}>
              <BadgeCheck className="h-4 w-4" />
              {t('runDetail.markPaid', { defaultValue: 'Already paid' })}
            </Button>
          )}
          <Button className="gap-2" disabled={busy || payStarted} onClick={onPay}>
            <Send className="h-4 w-4" />
            {t('runDetail.payEmployees')}
          </Button>
        </div>
      );
    case 'paid':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-success/10 px-3 py-2 text-sm font-medium text-success">
          <CheckCircle2 className="h-4 w-4" />
          {t('runDetail.paid')}
        </span>
      );
  }
};

const Lifecycle: React.FC<{ state: RunDetail['run']['state'] }> = ({ state }) => {
  const current = RUN_STATE_ORDER.indexOf(state);
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto">
      {RUN_STATE_ORDER.map((s, i) => {
        const done = i <= current;
        return (
          <React.Fragment key={s}>
            <span
              className={cn(
                'whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold capitalize',
                // `charcoal` is theme-aware (dark in light mode, light in dark mode), so a fixed
                // `text-white` became white-on-light in dark mode. `text-background` is the inverse,
                // keeping the reached-step pill readable in both themes.
                done ? 'bg-charcoal text-background' : 'bg-muted text-muted-foreground',
              )}
            >
              {RUN_STATE_META[s].label}
            </span>
            {i < RUN_STATE_ORDER.length - 1 && (
              <span className={cn('h-0.5 w-6 shrink-0', i < current ? 'bg-charcoal' : 'bg-border')} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const DetailSkeleton: React.FC = () => (
  <div className="space-y-6">
    <Skeleton className="h-5 w-32" />
    <Skeleton className="h-16 w-full" />
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-24 w-full" />
      ))}
    </div>
    <Skeleton className="h-80 w-full" />
  </div>
);
